import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  Platform,
  ActivityIndicator,
  TouchableOpacity,
  Pressable,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import { ADOPTION_WIDGETS, adoptionReportUrl } from '../utils/adoptionApi';
import { exerciseByIdUrl } from '../utils/exerciseApi';
import { hasSuperUserAccess } from '../utils/sessionUser';

const PAGE_SIZE = 50;

function formatCreatedAt(value) {
  if (value == null || value === '') return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function reportColumns(reportKey) {
  switch (reportKey) {
    case 'newly_created_exercises':
      return [
        { key: 'id', label: 'Id', width: 72, link: 'exercise' },
        { key: 'exercise_name', label: 'Name', width: 220, link: 'exercise' },
        { key: 'exercise_type_name', label: 'Exercise Type', width: 180 },
        { key: 'created_at', label: 'Created', width: 160 },
      ];
    case 'exercises_no_media':
      return [
        { key: 'id', label: 'Id', width: 72, link: 'exercise' },
        { key: 'exercise_name', label: 'Name', width: 220, link: 'exercise' },
        { key: 'exercise_type_name', label: 'Exercise Type', width: 180 },
      ];
    case 'exercise_types_no_exercises':
      return [
        { key: 'id', label: 'Id', width: 72, link: 'exerciseType' },
        { key: 'exercise_type_name', label: 'Name', width: 220, link: 'exerciseType' },
        { key: 'selection_source', label: 'Scope', width: 120 },
      ];
    case 'courses_no_exercise_types':
    case 'courses_no_exercises':
    case 'courses_no_stories':
      return [
        { key: 'id', label: 'Id', width: 72, link: 'course' },
        { key: 'course_name', label: 'Name', width: 280, link: 'course' },
      ];
    case 'courses_incomplete_content':
      return [
        { key: 'id', label: 'Id', width: 72, link: 'course' },
        { key: 'course_name', label: 'Name', width: 220, link: 'course' },
        { key: 'missing_fields', label: 'Missing', width: 200 },
      ];
    case 'grades_no_exercise_types':
    case 'grades_no_exercises':
      return [
        { key: 'id', label: 'Id', width: 72 },
        { key: 'grade_name', label: 'Name', width: 280 },
      ];
    default:
      return [{ key: 'id', label: 'Id', width: 80 }];
  }
}

function RecordsStyleTable({ columns, rows, theme, isDark, onCellPress, linkableKeys }) {
  const webLink = Platform.OS === 'web' ? { cursor: 'pointer' } : null;

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator
      {...(Platform.OS === 'web'
        ? { className: 'records-list-horizontal-scroll' }
        : {})}
    >
      <View
        style={[styles.table, { borderColor: isDark ? '#444' : '#dddbda' }]}
        {...(Platform.OS === 'web' ? { dataSet: { adoptionReportTable: 'true' } } : {})}
      >
        <View
          style={[
            styles.tr,
            styles.thRow,
            { backgroundColor: isDark ? '#2a2a2a' : '#fafaf9', borderColor: isDark ? '#444' : '#dddbda' },
          ]}
        >
          {columns.map((c) => (
            <Text
              key={c.key}
              style={[styles.th, { width: c.width, color: theme.colors.heading }]}
            >
              {c.label}
            </Text>
          ))}
        </View>
        {rows.length === 0 ? (
          <Text style={[styles.emptyHint, { color: theme.colors.text, padding: 16 }]}>
            No records match this report.
          </Text>
        ) : (
          rows.map((row, ri) => (
            <View
              key={String(row.id ?? ri)}
              style={[
                styles.tr,
                {
                  backgroundColor:
                    ri % 2 === 0
                      ? isDark
                        ? 'rgba(255,255,255,0.03)'
                        : '#ffffff'
                      : isDark
                        ? 'transparent'
                        : '#fafaf9',
                  borderColor: isDark ? '#333' : '#e5e5e5',
                },
              ]}
            >
              {columns.map((c) => {
                const isLink = c.link && linkableKeys.has(c.key);
                const val = row[c.key];
                const display =
                  c.key === 'created_at'
                    ? formatCreatedAt(val)
                    : val != null && val !== ''
                      ? String(val)
                      : '—';
                if (isLink) {
                  return (
                    <Pressable
                      key={c.key}
                      onPress={() => onCellPress(row, c.link)}
                      style={[styles.tdHit, { width: c.width }, webLink]}
                    >
                      <Text style={[styles.tdLink, { color: '#0176d3' }]} numberOfLines={2}>
                        {display}
                      </Text>
                    </Pressable>
                  );
                }
                return (
                  <Text
                    key={c.key}
                    style={[styles.td, { width: c.width, color: theme.colors.text }]}
                    numberOfLines={2}
                  >
                    {display}
                  </Text>
                );
              })}
            </View>
          ))
        )}
      </View>
    </ScrollView>
  );
}

export default function AdoptionReportsPage({ navigation, route }) {
  const theme = useTheme();
  const isDark = theme.isDarkMode;
  const stopwatchRef = useRef(null);
  const reportKey = route?.params?.reportKey;
  const [currentUser, setCurrentUser] = useState(route?.params?.user || null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const widgetMeta = ADOPTION_WIDGETS.find((w) => w.key === reportKey);
  const columns = reportColumns(reportKey);
  const linkableKeys = new Set(
    columns.filter((c) => c.link).map((c) => c.key)
  );

  useEffect(() => {
    const u = route?.params?.user;
    if (u) setCurrentUser(u);
    if (u && !hasSuperUserAccess(u)) {
      navigation.replace('MainPage', { user: u });
    }
  }, [route?.params?.user, navigation]);

  const load = useCallback(async (options = {}) => {
    const silent = options.silent === true;
    const u = currentUser || route?.params?.user;
    if (!u?.id || !hasSuperUserAccess(u) || !reportKey) {
      setLoading(false);
      return;
    }
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const res = await fetch(adoptionReportUrl(reportKey, u.id, page, PAGE_SIZE));
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setRows(Array.isArray(json.rows) ? json.rows : []);
      setTotal(json.total ?? 0);
    } catch (e) {
      setError(e.message || 'Failed to load report');
      if (!silent) {
        setRows([]);
        setTotal(0);
      }
    } finally {
      if (silent) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  }, [currentUser, route?.params?.user, reportKey, page]);

  const handleRefreshTable = useCallback(() => {
    void load({ silent: true });
  }, [load]);

  useEffect(() => {
    void load();
  }, [load]);

  const navParams = () => {
    const u = currentUser || route?.params?.user;
    return { userId: u?.id, user: u };
  };

  const openExerciseEditor = async (row) => {
    const u = currentUser || route?.params?.user;
    const base = navParams();
    let context = {
      selection_source: row.selection_source,
      course_id: row.course_id,
      grade_id: row.grade_id,
      exercise_type_id: row.exercise_type_id,
    };
    if (!context.selection_source) {
      try {
        const res = await fetch(exerciseByIdUrl(row.id, { viewerId: u?.id }));
        if (res.ok) {
          const ex = await res.json();
          context = {
            ...context,
            exercise_type_id: ex.exercise_type_id,
          };
        }
      } catch {
        /* use row context only */
      }
    }
    navigation.navigate('ExerciseBuilder', {
      ...base,
      openEditExerciseId: row.id,
      adoptionEditContext: context,
    });
  };

  const openExerciseTypeEditor = (row) => {
    navigation.navigate('ExerciseTypeBuilder', {
      ...navParams(),
      openEditExerciseTypeId: row.id,
      adoptionEditContext: {
        selection_source: row.selection_source,
        course_id: row.course_id,
        grade_id: row.grade_id,
      },
    });
  };

  const openCourseEditor = (row) => {
    navigation.navigate('CourseEditor', {
      ...navParams(),
      courseId: row.id,
    });
  };

  const openStoryBuilder = (row) => {
    navigation.navigate('StoryBuilder', {
      ...navParams(),
      initialCourseId: row.id,
    });
  };

  const handleCellPress = (row, linkType) => {
    if (linkType === 'exercise') {
      void openExerciseEditor(row);
    } else if (linkType === 'exerciseType') {
      openExerciseTypeEditor(row);
    } else if (linkType === 'course') {
      if (reportKey === 'courses_no_stories') {
        openStoryBuilder(row);
      } else {
        openCourseEditor(row);
      }
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  if (!hasSuperUserAccess(currentUser) && !hasSuperUserAccess(route?.params?.user)) {
    return null;
  }

  return (
    <View
      style={[styles.container, { backgroundColor: isDark ? '#181818' : '#f3f2f2' }]}
      {...(Platform.OS === 'web' ? { dataSet: { screen: 'adoption-reports' } } : {})}
    >
      <StatusBar style={isDark ? 'light' : 'dark'} />
      <View style={[styles.header, { backgroundColor: isDark ? theme.colors.background : '#fff' }]}>
        <View style={styles.hamburgerContainer}>
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
        </View>
        <View style={styles.breadcrumbContainer}>
          <TouchableOpacity onPress={() => navigation.navigate('Adoption', navParams())}>
            <Text style={[styles.backLink, { color: '#0176d3' }]}>← Adoption</Text>
          </TouchableOpacity>
          <Text style={[styles.breadcrumb, { color: theme.colors.heading }]}>
            Adoption Reports
          </Text>
          <Text style={[styles.breadcrumbSub, { color: theme.colors.text }]} numberOfLines={2}>
            {widgetMeta?.title || reportKey}
          </Text>
        </View>
        <ProfileIcon navigation={navigation} user={currentUser} />
      </View>

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        nestedScrollEnabled
        showsVerticalScrollIndicator
        keyboardShouldPersistTaps="handled"
        {...(Platform.OS === 'web'
          ? { dataSet: { adoptionReportsScroll: 'true' }, className: 'adoption-reports-vertical-scroll' }
          : {})}
      >
        {loading ? (
          <ActivityIndicator size="large" color="#0176d3" style={{ marginVertical: 24 }} />
        ) : null}
        {error ? (
          <View style={[styles.errorBox, { borderColor: '#ba0517' }]}>
            <Text style={styles.errorText}>{error}</Text>
          </View>
        ) : null}

        {!loading && !error ? (
          <>
            <View style={styles.recordCountRow}>
              <Text style={[styles.recordCount, { color: theme.colors.text }]}>
                {total} record{total === 1 ? '' : 's'}
              </Text>
              <TouchableOpacity
                style={styles.refreshButton}
                onPress={handleRefreshTable}
                disabled={refreshing}
                accessibilityRole="button"
                accessibilityLabel="Refresh report table"
              >
                <Text style={[styles.refreshIcon, refreshing && styles.refreshIconDisabled]}>🔄</Text>
              </TouchableOpacity>
            </View>
            <RecordsStyleTable
              columns={columns}
              rows={rows}
              theme={theme}
              isDark={isDark}
              onCellPress={handleCellPress}
              linkableKeys={linkableKeys}
            />
            <View style={styles.pagination}>
              <TouchableOpacity
                disabled={page <= 1}
                onPress={() => setPage((p) => Math.max(1, p - 1))}
                style={[styles.pageBtn, page <= 1 && styles.pageBtnDisabled]}
              >
                <Text style={styles.pageBtnText}>Previous</Text>
              </TouchableOpacity>
              <Text style={[styles.pageInfo, { color: theme.colors.text }]}>
                Page {page} of {totalPages}
              </Text>
              <TouchableOpacity
                disabled={page >= totalPages}
                onPress={() => setPage((p) => p + 1)}
                style={[styles.pageBtn, page >= totalPages && styles.pageBtnDisabled]}
              >
                <Text style={styles.pageBtnText}>Next</Text>
              </TouchableOpacity>
            </View>
          </>
        ) : null}
      </ScrollView>
      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#dddbda',
  },
  hamburgerContainer: { width: 44 },
  breadcrumbContainer: { flex: 1, paddingHorizontal: 8 },
  backLink: { fontSize: 13, fontWeight: '600', marginBottom: 4 },
  breadcrumb: { fontSize: 20, fontWeight: '700' },
  breadcrumbSub: { fontSize: 13, marginTop: 2 },
  scroll: { flex: 1, ...(Platform.OS === 'web' ? { minHeight: 0 } : {}) },
  scrollContent: { padding: 16, paddingBottom: 40, flexGrow: 1 },
  errorBox: { borderWidth: 1, borderRadius: 8, padding: 12, marginBottom: 12 },
  errorText: { color: '#ba0517' },
  recordCountRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  recordCount: { fontSize: 13, fontWeight: '600', flex: 1 },
  refreshButton: {
    padding: 6,
    marginLeft: 8,
  },
  refreshIcon: {
    fontSize: 24,
  },
  refreshIconDisabled: {
    opacity: 0.45,
  },
  table: { borderWidth: 1, borderRadius: 4, minWidth: '100%' },
  tr: { flexDirection: 'row', borderBottomWidth: StyleSheet.hairlineWidth },
  thRow: { borderBottomWidth: 1 },
  th: { padding: 10, fontSize: 12, fontWeight: '700', textTransform: 'uppercase' },
  td: { padding: 10, fontSize: 13 },
  tdHit: { padding: 10, justifyContent: 'center' },
  tdLink: { fontSize: 13, fontWeight: '600', textDecorationLine: 'underline' },
  emptyHint: { fontSize: 14 },
  pagination: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    marginTop: 16,
  },
  pageBtn: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 6,
    backgroundColor: '#0176d3',
  },
  pageBtnDisabled: { opacity: 0.4 },
  pageBtnText: { color: '#fff', fontWeight: '600', fontSize: 13 },
  pageInfo: { fontSize: 13 },
});
